

export class GoogleDocsService {
  private static readonly DOCUMENT_ID = '1881hjNoWETfsRUP6eGdb4apb6dozvgsoaOxUoIc1ccE';

  /**
   * Sanitizes the document content to prevent security issues
   * @param content - The raw document content
   * @returns string - The sanitized content
   */
  private static sanitizeContent(content: string): string {
    if (!content || typeof content !== 'string') {
      return '';
    }

    // Remove any HTML tags that might be present
    let sanitized = content.replace(/<[^>]*>/g, '');
    
    // Remove any script tags or dangerous content
    sanitized = sanitized.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    sanitized = sanitized.replace(/javascript:/gi, '');
    sanitized = sanitized.replace(/on\w+\s*=/gi, '');
    
    // Remove any control characters except newlines and tabs
    // eslint-disable-next-line no-control-regex
    sanitized = sanitized.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
    
    // Normalize whitespace (keep newlines but normalize spaces)
    sanitized = sanitized.replace(/[ \t]+/g, ' ');
    sanitized = sanitized.replace(/\n\s*\n\s*\n/g, '\n\n'); // Max 2 consecutive newlines
    
    // Trim whitespace
    sanitized = sanitized.trim();
    
    // Limit content length to prevent abuse
    const maxLength = 50000; // 50KB limit
    if (sanitized.length > maxLength) {
      sanitized = sanitized.substring(0, maxLength) + '\n\n[Content truncated due to length]';
    }
    
    return sanitized;
  }

  /**
   * Fetches the content from the Google Document using public export
   * @returns Promise<string> - The document content
   */
  static async fetchDocumentContent(): Promise<string> {
    try {
      console.log('Document ID:', this.DOCUMENT_ID);

      // Use Google Docs public export URL (no authentication required)
      const url = `https://docs.google.com/document/d/${this.DOCUMENT_ID}/export?format=txt`;
      
      console.log('Fetching from URL:', url);
      
      const response = await fetch(url);
      
      console.log('Response status:', response.status);
      console.log('Response status text:', response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response body:', errorText);
        
        if (response.status === 403) {
          throw new Error(`Access denied (403). Please ensure the Google Document is publicly accessible. Make sure the document is shared with "Anyone with the link can view" permissions.`);
        } else if (response.status === 404) {
          throw new Error(`Document not found (404). Please check the document ID: ${this.DOCUMENT_ID}.`);
        } else {
          throw new Error(`Failed to fetch document: ${response.status} ${response.statusText}.`);
        }
      }

      const rawContent = await response.text();
      const sanitizedContent = this.sanitizeContent(rawContent);
      
      console.log('Content sanitized successfully');
      return sanitizedContent;
    } catch (error) {
      console.error('Error fetching Google Document:', error);
      throw error;
    }
  }



  /**
   * Caches the document content to avoid repeated API calls
   * @param content - The document content to cache
   */
  static cacheContent(content: string): void {
    const cacheData = {
      content,
      timestamp: Date.now(),
      expiresAt: Date.now() + (5 * 60 * 1000) // Cache for 5 minutes
    };
    localStorage.setItem('googleDocsCache', JSON.stringify(cacheData));
  }

  /**
   * Gets cached content if it's still valid
   * @returns string | null - The cached content or null if expired/not found
   */
  static getCachedContent(): string | null {
    try {
      const cached = localStorage.getItem('googleDocsCache');
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      if (Date.now() > cacheData.expiresAt) {
        localStorage.removeItem('googleDocsCache');
        return null;
      }

      return cacheData.content;
    } catch (error) {
      console.error('Error reading cached content:', error);
      return null;
    }
  }

  /**
   * Main method to get document content with caching
   * @returns Promise<string> - The document content
   */
  static async getDocumentContent(): Promise<string> {
    // Try to get cached content first
    const cachedContent = this.getCachedContent();
    if (cachedContent) {
      return cachedContent;
    }

    // Fetch fresh content from API
    const content = await this.fetchDocumentContent();
    
    // Cache the new content
    this.cacheContent(content);
    
    return content;
  }


} 